/**
 * Pane API — the five /api/v1/pane/tiles calls, over the generated client.
 *
 * Bearer tokens are injected by the interceptor in `useApiClient`.
 */
import type { Client } from "@hey-api/client-fetch";
import {
  clearTiles as clearTilesCall,
  createTile as createTileCall,
  deleteTile as deleteTileCall,
  listTiles as listTilesCall,
  updateTile as updateTileCall,
} from "@/api";
import { apiErrorMessage } from "@/lib/api-error";
import type { CreateTileBody, PaneTile, UpdateTileBody } from "./types";

export async function listTiles(client: Client): Promise<PaneTile[]> {
  const { data } = await listTilesCall({ client, throwOnError: true });
  return data.tiles;
}

export async function createTile(client: Client, body: CreateTileBody): Promise<PaneTile> {
  const { data } = await createTileCall({ client, body, throwOnError: true });
  return data;
}

/**
 * OCC-protected update. Throws a TileUpdateError carrying the HTTP status,
 * so a 409 (stale `expected_version`) can be retried after a refresh.
 */
export async function updateTile(
  client: Client,
  tileId: string,
  body: UpdateTileBody,
): Promise<PaneTile> {
  const res = await updateTileCall({ client, path: { tile_id: tileId }, body });
  if (res.error !== undefined || !res.data) {
    // The status is on the response. This read it from the error *body*
    // ({detail}), which has none — so a 409 never counted as a conflict and
    // the retry-after-refresh path never ran (found by RW-1.18's port).
    throw new TileUpdateError(
      `Failed to update tile: ${apiErrorMessage(res.error)}`,
      res.response.status,
    );
  }
  return res.data;
}

export async function deleteTile(client: Client, tileId: string): Promise<void> {
  await deleteTileCall({ client, path: { tile_id: tileId }, throwOnError: true });
}

/** Remove every tile, or with `keepPinned` every unpinned one. */
export async function clearTiles(
  client: Client,
  keepPinned = false,
): Promise<{ removed: number }> {
  const { data } = await clearTilesCall({
    client,
    query: { keep_pinned: keepPinned },
    throwOnError: true,
  });
  return data;
}

/**
 * Tagged error so the optimistic update layer can distinguish
 * "stale version — re-fetch and retry" (409) from "transport
 * failure — surface to user".
 */
export class TileUpdateError extends Error {
  constructor(
    message: string,
    public status?: number,
  ) {
    super(message);
    this.name = "TileUpdateError";
  }
  get isConflict(): boolean {
    return this.status === 409;
  }
}
